<template>
  <div>
    <!-- LOADING -->
    <div v-if="loading" class="screen-bg d-flex align-items-center justify-content-center">
      <div class="spinner-border text-light" role="status" style="width: 48px; height: 48px;">
        <span class="sr-only"></span>
      </div>
    </div>

    <!-- 404 -->
    <div v-else-if="notFound" class="screen-bg d-flex align-items-center justify-content-center">
      <div style="text-align:center; color:white; font-family: VarelaRound, sans-serif; padding: 32px;">
        <div style="font-size: 72px; margin-bottom: 16px;">🔍</div>
        <div style="font-size: 28px; font-weight: 800; margin-bottom: 8px;">404</div>
        <div style="font-size: 18px; opacity: 0.8; margin-bottom: 24px;">Parcours introuvable</div>
        <div style="font-size: 14px; opacity: 0.55; margin-bottom: 32px;">
          L'adresse <code
            style="background:rgba(255,255,255,0.1);padding:2px 8px;border-radius:6px;">{{ $route.params.gameName }}</code>
          n'existe pas.
        </div>
        <button @click="$router.push('/')"
          style="background:white;color:#1a3a8f;border:none;border-radius:50px;padding:12px 32px;font-size:16px;font-weight:700;cursor:pointer;">
          ← Retour à l'accueil
        </button>
      </div>
    </div>

    <!-- MAIN SCREEN -->
    <div v-else class="screen-bg" :dir="lang === 'he' || lang === 'ar' ? 'rtl' : 'ltr'" :style="bgStyle">

      <!-- CENTRAL CARD -->
      <div class="main-card">
        <div class="game-name">{{ gameName }}</div>

        <div class="beware-row">
          <span class="beware-icon">📍</span>
          <div>
            <div class="beware-title">{{ t.beware }}</div>
            <div class="beware-text">{{ startPointText }}</div>
          </div>
        </div>

        <div class="field-label">{{ t.pleaseToKnowYou }}</div>
        <input v-model="userName" :placeholder="t.pseudoPlaceholder" class="name-input" @keyup.enter="startGame" />

        <div class="lang-row" v-if="languages.length > 1">
          <button v-if="languages.includes('he')" :class="['lang-btn', { active: lang === 'he' }]"
            @click="lang = 'he'">עברית</button>
          <button v-if="languages.includes('ar')" :class="['lang-btn', { active: lang === 'ar' }]"
            @click="lang = 'ar'">عربي</button>
          <button v-if="languages.includes('fr')" :class="['lang-btn', { active: lang === 'fr' }]"
            @click="lang = 'fr'">FR</button>
          <button v-if="languages.includes('en')" :class="['lang-btn', { active: lang === 'en' }]"
            @click="lang = 'en'">EN</button>
        </div>

        <div v-if="error" class="ge-error">{{ error }}</div>
      </div>

      <!-- BOTTOM BAR -->
      <div class="ge-start-bar">
        <button class="ge-start-btn" @click="startGame">
          🚀 {{ t.startBtn }}
        </button>
      </div>

      <!-- ALERT NO NAME -->
      <div v-if="showAlertNoName" class="alert-overlay" @click="showAlertNoName = false">
        <div class="alert-card">
          <div class="alert-oups">{{ t.oups }}…</div>
          <div class="alert-msg">{{ t.noPassword }}</div>
          <div class="alert-ok">{{ t.gotIt }}</div>
        </div>
      </div>

      <!-- INTRO MESSAGE -->
      <div v-if="showIntro" class="tuto-overlay">
        <div class="tuto-proposal-card">
          <div class="tuto-emoji">🗺️</div>
          <div class="tuto-title" :dir="(lang === 'he' || lang === 'ar') ? 'rtl' : 'ltr'">{{ t.introTitle }}</div>
          <div class="intro-rules" :dir="(lang === 'he' || lang === 'ar') ? 'rtl' : 'ltr'">
            <div class="intro-rule" v-for="rule in t.introRules" :key="rule.icon">
              <span class="intro-rule-icon">{{ rule.icon }}</span>
              <span class="intro-rule-text">{{ rule.text }}</span>
            </div>
          </div>
          <button class="tuto-btn-yes" :dir="(lang === 'he' || lang === 'ar') ? 'rtl' : 'ltr'" @click="showIntro = false; showTutorialProposal = true">{{ t.introBtn }}</button>
        </div>
      </div>

      <!-- TUTORIAL PROPOSAL -->
      <div v-if="showTutorialProposal && !showTutorial" class="tuto-overlay">
        <div class="tuto-proposal-card">
          <div class="tuto-emoji">🗺️</div>
          <div class="tuto-title">{{ t.tutorialTitle }}</div>
          <div class="tuto-subtitle">{{ t.tutorialSubtitle }}</div>
          <button class="tuto-btn-yes" @click="startInGameTutorial">{{ t.tutorialYes }}</button>
          <button class="tuto-btn-skip" @click="launchGame">{{ t.tutorialSkip }}</button>
        </div>
      </div>


    </div>
  </div>
</template>

<script>
/* eslint-disable */
export default {
  data() {
    return {
      gameName: '',
      userName: localStorage.getItem('myNameOuchui') || '',
      lang: 'he',
      languages: ['he'],
      loading: true,
      notFound: false,
      bgImageUrl: '',
      starting: false,
      error: '',
      wazeLink: '',
      startLocationName: '',
      showAlertNoName: false,
      showIntro: false,
      showTutorialProposal: false,
      showTutorial: false,
      tutorialStep: 0,
    };
  },

  computed: {
    t() {
      const translations = {
        he: {
          pleaseToKnowYou: 'נשמח להכיר אותך',
          pseudoPlaceholder: 'הכנס שם...',
          beware: 'שימו לב!',
          startPoint: 'הגיעו לנקודת ההתחלה עם Waze',
          startLocationPrefix: 'הגיעו לנקודת ההתחלה:',
          startBtn: 'מתחילים',
          loading: 'טוען...',
          oups: 'אופס',
          noPassword: 'נראה ששכחת להכניס את שמך',
          gotIt: 'הבנתי',
          tutorialTitle: 'רוצים להכיר את המשחק?',
          tutorialSubtitle: 'צפו במדריך קצר לפני שמתחילים',
          tutorialYes: '📖 כן, הראו לי',
          tutorialSkip: 'דלג, מתחיל ישירות ←',
          tutorialNext: 'הבא ←',
          tutorialGo: '🚀 מתחילים!',
          introTitle: 'ברוכים הבאים!',
          introRules: [
            { icon: '📍', text: 'תעברו מנקודה לנקודה לאורך המסלול.' },
            { icon: '✅', text: 'תשובה נכונה מוסיפה נקודות, תשובה שגויה מורידה נקודות.' },
            { icon: '🏆', text: 'בסוף המסלול תוכלו לראות את הדירוג שלכם בין כל המשתתפים.' },
          ],
          introBtn: 'בואו נתחיל!',
          tutorialSlides: [
            { icon: '🗺️', title: 'ברוכים הבאים!', text: 'המשחק מוביל אתכם בין נקודות עניין ברחבי האזור. בכל נקודה תגלו תוכן מרתק ואתגר מיוחד. הגיעו לכל הנקודות לפי הסדר ותצברו נקודות!' },
            { icon: '📱', title: 'ניווט עם המפה', text: 'על המסך תראו מפה עם מיקומכם הנוכחי. כשתגיעו לאזור הנקודה — המשחק יתקדם אוטומטית. אפשר גם ללחוץ על כפתור Waze בתפריט כדי לנווט ישירות לנקודה.' },
            { icon: '🖼️', title: 'סדרת תמונות', text: 'לעיתים תקבלו מספר תמונות הקשורות למקום שבו אתם נמצאים — בחנו אותן היטב, הן עשויות לעזור לכם לענות על השאלות!' },
            { icon: '🔤', title: 'השלמת מילה חסרה', text: 'תראו פוסטר או שלט עם מילה מוסתרת. המשימה שלכם — לגלות מהי המילה החסרה בעזרת הרמזים שסביבכם!' },
            { icon: 'ℹ️', title: 'מידע על המקום', text: 'לפני האתגר תקבלו לפעמים מידע מרתק על המקום — טקסט, תמונה, סרטון או הקלטה. כדאי לקרוא בעיון, זה יעזור לכן לאחר מכן!' },
            { icon: '🎯', title: 'האתגר', text: 'אחרי המידע מגיע האתגר! שלוש אפשרויות: שאלות ידע, פאזל שצריך להרכיב, או התאמת תמונות למילים. ענו נכון כדי לצבור נקודות מקסימום.' },
            { icon: '🏆', title: 'ניקוד ותוצאות', text: 'ככל שתגיעו מהר יותר ותענו נכון יותר — כך תצברו יותר נקודות. בסוף המסלול תוכלו לראות את הציון שלכם ולהשוות עם שאר הקבוצות!' },
          ],
        },
        ar: {
          pleaseToKnowYou: 'من الرائع أن ألتقي بك',
          pseudoPlaceholder: 'أدخل اسمك...',
          beware: 'انتباه!',
          startPoint: 'توجهوا لنقطة البداية عبر Waze',
          startLocationPrefix: 'توجهوا إلى نقطة البداية:',
          startBtn: 'ابدأ',
          loading: 'جارٍ التحميل...',
          oups: 'عفوا',
          noPassword: 'يبدو أنك نسيت إدخال اسمك',
          gotIt: 'حسناً',
          tutorialTitle: 'هل تريد التعرف على اللعبة؟',
          tutorialSubtitle: 'شاهد دليلاً قصيراً قبل البدء',
          tutorialYes: '📖 نعم، أرني',
          tutorialSkip: 'تخطي، ابدأ مباشرة ←',
          tutorialNext: 'التالي ←',
          tutorialGo: '🚀 لنبدأ!',
          introTitle: '!مرحباً بكم',
          introRules: [
            { icon: '📍', text: 'ستنتقلون من نقطة إلى نقطة على طول المسار.' },
            { icon: '✅', text: 'إجابة صحيحة تضيف نقاطاً، وإجابة خاطئة تُنقص منها.' },
            { icon: '🏆', text: 'في نهاية المسار ستتمكنون من رؤية ترتيبكم بين جميع المشاركين.' },
          ],
          introBtn: '!هيا نبدأ',
          tutorialSlides: [
            { icon: '🗺️', title: 'مرحباً بكم!', text: 'تقودكم اللعبة بين نقاط اهتمام في المنطقة. في كل نقطة ستكتشفون محتوى مثيراً وتحدياً خاصاً. تنقلوا بين جميع النقاط بالترتيب واجمعوا النقاط!' },
            { icon: '📱', title: 'التنقل بالخريطة', text: 'ستظهر على الشاشة خريطة بموقعكم الحالي. حين تصلون إلى منطقة النقطة ينتقل المشغل تلقائياً. يمكنكم أيضاً الضغط على زر Waze في القائمة للتنقل مباشرة.' },
            { icon: '🖼️', title: 'سلسلة الصور', text: 'أحياناً ستحصلون على عدة صور مرتبطة بالمكان الذي أنتم فيه — تأملوها جيداً فقد تساعدكم في الإجابة على الأسئلة!' },
            { icon: '🔤', title: 'الكلمة الناقصة', text: 'ستشاهدون ملصقاً أو لافتة تحتوي على كلمة مخفية. مهمتكم اكتشاف الكلمة الناقصة بمساعدة القرائن المحيطة بكم!' },
            { icon: 'ℹ️', title: 'معلومات عن المكان', text: 'قبل التحدي قد تحصلون على معلومات مثيرة عن المكان — نص أو صورة أو مقطع فيديو أو تسجيل صوتي. اقرأوا بعناية فهذا سيساعدكم لاحقاً!' },
            { icon: '🎯', title: 'التحدي', text: 'بعد المعلومات يأتي التحدي! ثلاثة احتمالات: أسئلة معرفية، أحجية للتركيب، أو مطابقة صور بكلمات. أجيبوا صحيحاً لتجمعوا أكبر عدد من النقاط.' },
            { icon: '🏆', title: 'النتيجة', text: 'كلما وصلتم أسرع وأجبتم بشكل أصح، زادت نقاطكم. في نهاية المسار ستتمكنون من رؤية نتيجتكم ومقارنتها مع بقية الفرق!' },
          ],
        },
        fr: {
          pleaseToKnowYou: 'On se fait un plaisir de vous connaître :',
          pseudoPlaceholder: 'Entre ton prénom...',
          beware: 'Attention !',
          startPoint: 'Rejoignez le point de départ avec Waze',
          startLocationPrefix: 'Rendez-vous à l\'endroit du début du parcours :',
          startBtn: 'ON Y EST!',
          loading: 'Chargement...',
          oups: 'Oups',
          noPassword: "Il semblerait que vous avez oublié d'entrer un pseudo...",
          gotIt: "Ok, j'ai compris",
          tutorialTitle: 'Envie de découvrir le jeu ?',
          tutorialSubtitle: 'Regardez ce petit guide avant de commencer',
          tutorialYes: '📖 Oui, montrez-moi',
          tutorialSkip: 'Passer, démarrer directement →',
          tutorialNext: 'Suivant →',
          tutorialGo: '🚀 C\'est parti !',
          introTitle: 'Bienvenue !',
          introRules: [
            { icon: '📍', text: 'Vous allez vous déplacer de point en point tout au long du parcours.' },
            { icon: '✅', text: 'Une bonne réponse rapporte des points, une mauvaise réponse en retire.' },
            { icon: '🏆', text: 'À la fin du parcours, vous pourrez voir votre classement parmi tous les participants.' },
          ],
          introBtn: 'C\'est parti !',
          tutorialSlides: [
            { icon: '🗺️', title: 'Bienvenue !', text: 'Le jeu vous emmène découvrir des points d\'intérêt dans la région. À chaque point, un contenu fascinant et un défi vous attendent. Parcourez toutes les étapes dans l\'ordre et accumulez les points !' },
            { icon: '📱', title: 'Navigation sur la carte', text: 'Une carte avec votre position s\'affiche à l\'écran. Quand vous approchez d\'un point, le jeu avance automatiquement. Vous pouvez aussi appuyer sur le bouton Waze dans le menu pour naviguer directement vers le point.' },
            { icon: '🖼️', title: 'Série d\'images', text: 'Parfois, plusieurs photos liées au lieu où vous vous trouvez vous seront présentées — observez-les attentivement, elles peuvent vous aider à répondre aux questions !' },
            { icon: '🔤', title: 'Mot manquant', text: 'Vous verrez une affiche ou un panneau avec un mot masqué. Votre mission : deviner le mot manquant en vous aidant des indices qui vous entourent !' },
            { icon: 'ℹ️', title: 'Informations sur le lieu', text: 'Avant le défi, vous recevrez parfois des informations passionnantes sur l\'endroit — texte, photo, vidéo ou enregistrement audio. Lisez attentivement, cela vous aidera ensuite !' },
            { icon: '🎯', title: 'Le défi', text: 'Après les infos vient le défi ! Trois possibilités : des questions de connaissance, un puzzle à reconstituer, ou une association d\'images à des mots. Répondez juste pour marquer un maximum de points.' },
            { icon: '🏆', title: 'Score et résultats', text: 'Plus vous arrivez vite et répondez correctement, plus vous marquez de points. À la fin du parcours, découvrez votre score et comparez-vous aux autres groupes !' },
          ],
        },
        en: {
          pleaseToKnowYou: "Nice to meet you!",
          pseudoPlaceholder: 'Enter your name...',
          beware: 'Heads up!',
          startPoint: 'Make your way to the starting point with Waze',
          startLocationPrefix: 'Make your way to the starting location:',
          startBtn: "LET'S GO!",
          loading: 'Loading...',
          oups: 'Oops',
          noPassword: "Looks like you forgot to enter your name...",
          gotIt: "Got it!",
          tutorialTitle: 'Want to learn how to play?',
          tutorialSubtitle: 'Watch a quick guide before you start',
          tutorialYes: '📖 Yes, show me',
          tutorialSkip: 'Skip, start directly →',
          tutorialNext: 'Next →',
          tutorialGo: '🚀 Let\'s go!',
          introTitle: 'Welcome!',
          introRules: [
            { icon: '📍', text: 'You\'ll move from point to point along the trail.' },
            { icon: '✅', text: 'A correct answer earns points, a wrong answer deducts them.' },
            { icon: '🏆', text: 'At the end of the trail, you\'ll be able to see your ranking among all participants.' },
          ],
          introBtn: 'Let\'s go!',
          tutorialSlides: [
            { icon: '🗺️', title: 'Welcome!', text: 'The game takes you to discover points of interest across the area. At each stop, fascinating content and a challenge await you. Visit all the points in order and rack up the points!' },
            { icon: '📱', title: 'Map navigation', text: 'A map showing your current location appears on screen. When you get close enough to a point, the game advances automatically. You can also tap the Waze button in the menu to navigate directly to the point.' },
            { icon: '🖼️', title: 'Image series', text: 'Sometimes you\'ll be shown several photos related to the place you\'re at — look carefully, they may help you answer the questions!' },
            { icon: '🔤', title: 'Missing word', text: 'You\'ll see a poster or sign with a hidden word. Your mission: figure out the missing word using the clues around you!' },
            { icon: 'ℹ️', title: 'Place information', text: 'Before the challenge, you may receive fascinating information about the location — text, photo, video or audio recording. Read carefully, it will help you later!' },
            { icon: '🎯', title: 'The challenge', text: 'After the info comes the challenge! Three possibilities: knowledge questions, a puzzle to assemble, or matching images to words. Answer correctly to score maximum points.' },
            { icon: '🏆', title: 'Score & results', text: 'The faster you arrive and the more correctly you answer, the more points you score. At the end of the trail, see your final score and compare with the other groups!' },
          ],
        },
      };
      return translations[this.lang] || translations.he;
    },
    startPointText() {
      if (this.startLocationName) {
        return this.t.startLocationPrefix + ' ' + this.startLocationName;
      }
      return this.t.startPoint;
    },

    bgStyle() {
      if (!this.bgImageUrl) return {};
      return {
        backgroundImage: `linear-gradient(160deg, rgba(0,30,90,0.4), rgba(0,80,180,0.25)), url("${this.bgImageUrl}")`,
        backgroundSize: 'cover',
        backgroundPosition: 'center 60px',
      };
    },
  },

  async mounted() {
    const routeGameName = this.$route?.params?.gameName;
    if (!routeGameName) { this.$router.push('/'); return; }

    // Resume or reset logic
    const savedGame = localStorage.getItem('navigationName');
    const savedName = localStorage.getItem('myNameOuchui');
    const savedScreen = localStorage.getItem('showMainScreen') || localStorage.getItem('showPickedItem') || localStorage.getItem('showPointInformation');

    if (savedGame && savedName && savedScreen) {
      if (savedGame === routeGameName) {
        // Same game — resume directly
        this.$router.push('/play');
        return;
      } else {
        // Different game — clear everything
        const keysToKeep = [];
        const allKeys = Object.keys(localStorage);
        allKeys.forEach(k => { if (!keysToKeep.includes(k)) localStorage.removeItem(k); });
      }
    }

    const firebase = require('firebase/app').default;
    try {
      const [nameSnap, displaySnap, pointSnap, langsSnap, bgSnap, startLocSnap, frPointSnap] = await Promise.all([
        firebase.database().ref(routeGameName + '/navigationName').once('value'),
        firebase.database().ref(routeGameName + '/displayName').once('value'),
        firebase.database().ref(routeGameName + '/he/points/1').once('value'),
        firebase.database().ref(routeGameName + '/languages').once('value'),
        firebase.database().ref(routeGameName + '/bgImage').once('value'),
        firebase.database().ref(routeGameName + '/startLocationName').once('value'),
        firebase.database().ref(routeGameName + '/fr/points/1').once('value'),
      ]);

      if (nameSnap.val() === null && langsSnap.val() === null && pointSnap.val() === null && frPointSnap.val() === null) {
        this.notFound = true;
        this.loading = false;
        return;
      }

      this.gameName = displaySnap.val() || nameSnap.val() || routeGameName;
      if (langsSnap.val()) {
        this.languages = langsSnap.val();
      } else if (frPointSnap.val()) {
        this.languages = ['fr'];
      } else {
        this.languages = ['he'];
      }
      this.lang = this.languages[0] || 'he';
      this.bgImageUrl = bgSnap.val() || '';
      this.startLocationName = startLocSnap.val() || '';

      const firstPoint = pointSnap.val();
      if (firstPoint && firstPoint.lat && firstPoint.lng) {
        const lat = encodeURIComponent(firstPoint.lat);
        const lng = encodeURIComponent(firstPoint.lng);
        this.wazeLink = `https://ul.waze.com/ul?ll=${lat}%2C${lng}&navigate=yes&utm_campaign=default&utm_source=waze_website&utm_medium=lm_share_location`;
        this.$store.commit('setWazeLink', this.wazeLink);
      }
    } catch {
      this.gameName = routeGameName;
    }
    this.loading = false;
  },

  beforeDestroy() {
    this.$store.commit('setWazeLink', '');
  },

  methods: {
    startGame() {
      const name = this.userName.trim();
      if (!name) { this.showAlertNoName = true; return; }
      this.showIntro = true;
      this.tutorialStep = 0;
      this.showTutorial = false;
    },

    startInGameTutorial() {
      localStorage.setItem('inGameTutorial', 'true');
      localStorage.setItem('tutorialScreensWanted', 'true');
      this.launchGame();
    },

    async launchGame() {
      if (this.starting) return;
      this.starting = true;
      this.showTutorialProposal = false;
      this.showTutorial = false;
      this.loading = true;
      this.error = '';

      const routeGameName = this.$route.params.gameName;
      const name = this.userName.trim();
      localStorage.setItem('myNameOuchui', name);
      localStorage.setItem('navigationName', routeGameName);
      this.$store.commit('switchLang', this.lang);

      try {
        await this.$store.dispatch('createNewUser', { navigationName: routeGameName, userName: name });
        await this.$store.dispatch('fetchGameData', { navigationName: routeGameName, userName: name, groupColorEnglish: 'blue' });
        this.$store.commit('setWazeLink', '');
        this.$router.push('/play');
      } catch (e) {
        this.error = 'Erreur lors du chargement du jeu. Réessaie.';
        this.starting = false;
      }
    },
  },
};
</script>

<style scoped>
.screen-bg {
  position: fixed;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  width: 100vw;
  max-width: 590px;
  height: 100vh;
  height: 100dvh;
  z-index: 9999;
  background:
    linear-gradient(160deg, rgba(0, 30, 90, 0.4), rgba(0, 80, 180, 0.25)),
    url("../../static/img/paysage.jpeg") no-repeat center top;
  background-size: cover;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 70px 20px 110px;
  box-sizing: border-box;
  overflow: visible;
}

.main-card {
  width: 88%;
  max-width: 400px;
  background: linear-gradient(160deg, rgba(0, 50, 140, 0.72), rgba(0, 100, 210, 0.58));
  border: 1.5px solid rgba(120, 200, 255, 0.3);
  /* backdrop-filter: blur(18px); */
  border-radius: 24px;
  padding: 24px 20px 20px;
  display: flex;
  flex-direction: column;
  gap: 16px;
  box-sizing: border-box;
  box-shadow: 0 8px 32px rgba(0, 60, 180, 0.35), 0 0 0 1px rgba(255, 255, 255, 0.05) inset;
}

.game-name {
  font-family: VarelaRound, system-ui, sans-serif;
  font-size: 26px;
  font-weight: 900;
  color: #ffd166;
  text-align: center;
  padding-bottom: 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
  text-shadow: 0 2px 12px rgba(255, 180, 0, 0.5), 0 0 30px rgba(255, 220, 0, 0.2);
}

.beware-row {
  display: flex;
  align-items: flex-start;
  gap: 10px;
  background: rgba(255, 180, 50, 0.12);
  border: 1px solid rgba(255, 180, 50, 0.25);
  border-radius: 12px;
  padding: 12px 14px;
}

.beware-icon {
  font-size: 18px;
  line-height: 1.4;
  flex-shrink: 0;
}

.beware-title {
  color: #ffd166;
  font-weight: 800;
  font-size: 16px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 2px;
}

.beware-text {
  color: rgba(255, 255, 255, 0.9);
  font-size: 16px;
  line-height: 1.5;
}

.field-label {
  color: rgba(255, 255, 255, 0.95);
  font-size: 18px;
  font-weight: 700;
  text-align: center;
}

.name-input {
  width: 100%;
  height: 52px;
  border-radius: 14px;
  border: 1.5px solid rgba(120, 200, 255, 0.4);
  background: rgba(255, 255, 255, 0.55);
  color: #0a1a3a;
  font-size: 18px;
  text-align: center;
  outline: none;
  font-family: VarelaRound, system-ui, sans-serif;
  padding: 0 16px;
  box-sizing: border-box;
  transition: border 0.2s, background 0.2s;
}

.name-input::placeholder {
  color: rgba(10, 26, 58, 0.5);
}

.name-input:focus {
  border-color: rgba(120, 210, 255, 0.9);
  background: rgba(255, 255, 255, 0.7);
}

.lang-row {
  display: flex;
  gap: 8px;
  justify-content: center;
}

.lang-btn {
  padding: 8px 18px;
  border-radius: 50px;
  border: 1.5px solid rgba(120, 200, 255, 0.4);
  background: rgba(255, 255, 255, 0.08);
  color: rgba(255, 255, 255, 0.75);
  font-size: 13px;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s;
  font-family: VarelaRound, system-ui, sans-serif;
}

.lang-btn.active {
  background: #ffd166;
  color: #1a3a8f;
  border-color: #ffd166;
  box-shadow: 0 2px 8px rgba(255, 180, 0, 0.4);
}

.ge-error {
  color: #ff6b6b;
  font-size: 13px;
  font-weight: bold;
  text-align: center;
}

/* ===== BOTTOM BAR ===== */
.bottomBar {
  background: linear-gradient(90deg, #0065a3, #0090e3);
  box-shadow: 0 -4px 20px rgba(0, 100, 200, 0.35);
  position: fixed;
  bottom: 0;
  width: 100vw;
  max-width: 590px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 10000;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  margin: 0;
  padding-top: 8px;
  padding-bottom: max(16px, env(safe-area-inset-bottom));
  box-sizing: border-box;
  min-height: 80px;
}

.startText {
  color: white;
  font-weight: bold;
  text-align: center;
  letter-spacing: 0.5px;
  margin-top: 2px;
}

.startButton {
  height: 80px;
  width: 80px;
  background: linear-gradient(170deg, #ffd166, #fcf9f7);
  box-shadow: 0 4px 0 rgba(0, 0, 0, 0.3), 0 6px 16px rgba(255, 160, 50, 0.4);
  color: white;
  position: relative;
  bottom: 47px;
  border-radius: 100%;
  cursor: pointer;
  transition: transform 0.1s, box-shadow 0.1s;
}

.startButton:active {
  transform: translateY(4px);
  box-shadow: 0 0 0 rgba(0, 0, 0, 0.3), 0 2px 8px rgba(255, 160, 50, 0.3);
}

/* ===== ALERT NO NAME ===== */
.alert-overlay {
  position: fixed;
  inset: 0;
  z-index: 10001;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(4px);
  padding-bottom: 90px;
}

.alert-card {
  background: rgba(0, 0, 0, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 20px;
  padding: 24px 24px 20px;
  width: 88%;
  max-width: 400px;
  text-align: center;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  animation: slideUp 0.2s ease-out;
}

.alert-oups {
  font-family: VarelaRound, system-ui, sans-serif;
  font-size: 32px;
  font-weight: 900;
  color: #ffd166;
}

.alert-msg {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.9);
  line-height: 1.5;
}

.alert-ok {
  margin-top: 6px;
  background: #3369bc;
  color: white;
  font-family: VarelaRound, system-ui, sans-serif;
  font-weight: 800;
  font-size: 15px;
  border-radius: 50px;
  padding: 11px 40px;
  cursor: pointer;
  transition: background 0.15s, transform 0.1s;
  border: none;
}

.alert-ok:active {
  transform: scale(0.97);
  background: #1a3a8f;
}

/* ===== TUTORIAL OVERLAY ===== */
.tuto-overlay {
  position: fixed;
  inset: 0;
  z-index: 10002;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  padding-bottom: 90px;
}

/* Proposal card */
.tuto-proposal-card {
  background: rgba(0, 0, 0, 0.65);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  padding: 28px 24px 22px;
  width: 88%;
  max-width: 400px;
  text-align: center;
  color: white;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  animation: slideUp 0.25s ease-out;
}

.tuto-emoji {
  font-size: 48px;
}

.tuto-title {
  font-family: VarelaRound, system-ui, sans-serif;
  font-size: 20px;
  font-weight: 900;
}

.tuto-subtitle {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.75);
}

.tuto-btn-yes {
  width: 100%;
  padding: 13px;
  background: #3369bc;
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 16px;
  font-weight: 800;
  font-family: VarelaRound, system-ui, sans-serif;
  cursor: pointer;
  transition: background 0.15s;
}

.tuto-btn-yes:hover {
  background: #1a3a8f;
}

.tuto-btn-skip {
  background: transparent;
  border: none;
  color: rgba(255, 255, 255, 0.55);
  font-size: 14px;
  cursor: pointer;
  padding: 4px;
  font-family: VarelaRound, system-ui, sans-serif;
  text-decoration: underline;
}

/* Intro rules */
.intro-rules {
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  margin: 4px 0 8px;
}
.intro-rule {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  background: rgba(255, 255, 255, 0.07);
  border-radius: 12px;
  padding: 11px 14px;
  text-align: start;
}
.intro-rule-icon {
  font-size: 20px;
  flex-shrink: 0;
  line-height: 1.4;
}
.intro-rule-text {
  font-size: 14px;
  line-height: 1.5;
  color: rgba(255, 255, 255, 0.9);
}

/* Slides card */
.tuto-slides-card {
  background: rgba(0, 0, 0, 0.7);
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 24px;
  padding: 24px 24px 20px;
  width: 88%;
  max-width: 400px;
  color: white;
  display: flex;
  flex-direction: column;
  gap: 18px;
  animation: slideUp 0.25s ease-out;
}

.tuto-progress {
  display: flex;
  justify-content: center;
  gap: 8px;
}

.tuto-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.3);
  transition: background 0.2s;
}

.tuto-dot.active {
  background: #3369bc;
  width: 24px;
  border-radius: 4px;
}

.tuto-slide {
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  padding: 8px 0;
}

.tuto-slide-icon {
  font-size: 44px;
}

.tuto-slide-title {
  font-family: VarelaRound, system-ui, sans-serif;
  font-size: 18px;
  font-weight: 900;
}

.tuto-slide-text {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.82);
  line-height: 1.6;
}

.tuto-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.tuto-nav-spacer {
  width: 44px;
}

.tuto-nav-back {
  width: 44px;
  height: 44px;
  border-radius: 50%;
  border: 1.5px solid rgba(255, 255, 255, 0.3);
  background: transparent;
  color: white;
  font-size: 18px;
  cursor: pointer;
}

.tuto-nav-next {
  padding: 10px 22px;
  background: #3369bc;
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  font-family: VarelaRound, system-ui, sans-serif;
  cursor: pointer;
}

.tuto-nav-start {
  padding: 10px 22px;
  background: #27ae60;
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 14px;
  font-weight: 700;
  font-family: VarelaRound, system-ui, sans-serif;
  cursor: pointer;
}

@keyframes slideUp {
  from {
    transform: translateY(30px);
    opacity: 0;
  }

  to {
    transform: translateY(0);
    opacity: 1;
  }
}

@font-face {
  font-family: VarelaRound;
  src: url(../../static/fonts/VarelaRound-Regular.ttf);
}

.ge-start-bar {
  position: absolute;
  bottom: 30px;
  left: 0;
  right: 0;
  padding: 0 24px;
  display: flex;
  justify-content: center;
  z-index: 10000;
}

.ge-start-btn {

  width: 100%;
  max-width: 340px;
  padding: 18px 0;
  border: none;
  border-radius: 18px;
  background: linear-gradient(135deg, #00c853, #1de9b6);
  color: #fff;
  font-size: 22px;
  font-weight: 700;
  font-family: VarelaRound, system-ui, sans-serif;
  letter-spacing: 0.5px;
  cursor: pointer;
  box-shadow: 0 6px 24px rgba(0, 200, 83, 0.45);
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.ge-start-btn:active {
  transform: scale(0.96);
  box-shadow: 0 3px 12px rgba(0, 200, 83, 0.35);
}
</style>
